import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  Check, 
  Calculator, 
  HelpCircle, 
  Info, 
  MapPin, 
  ArrowRight, 
  ShieldCheck, 
  Sparkles, 
  Clock, 
  Paintbrush, 
  AlertTriangle,
  Sliders,
  CheckCircle2
} from 'lucide-react'

export default function Prisestimater() {
  // Calculator states
  const [area, setArea] = useState(100)
  const [includeCeilings, setIncludeCeilings] = useState(false)
  const [includeWoodwork, setIncludeWoodwork] = useState(false)
  const [condition, setCondition] = useState('middel') // god, middel, dårlig
  const [estimatedMin, setEstimatedMin] = useState(17500)
  const [estimatedMax, setEstimatedMax] = useState(28000)
  const [showAllPrices, setShowAllPrices] = useState(false)

  // Calculate price estimate based on inputs
  useEffect(() => {
    // Base price per m2: 170 - 280 kr
    let baseMin = 170
    let baseMax = 280

    // Adjustments for ceilings and woodwork
    let multiplier = 1.0
    if (includeCeilings) multiplier += 0.3
    if (includeWoodwork) multiplier += 0.2

    // Adjustments for wall condition
    let conditionMultiplier = 1.0
    if (condition === 'god') conditionMultiplier = 0.9
    if (condition === 'dårlig') conditionMultiplier = 1.35

    let calculatedMin = area * baseMin * multiplier * conditionMultiplier
    let calculatedMax = area * baseMax * multiplier * conditionMultiplier

    // Round to nearest 500 kr for clean numbers
    const roundTo500 = (num) => Math.round(num / 500) * 500

    setEstimatedMin(roundTo500(calculatedMin))
    setEstimatedMax(roundTo500(calculatedMax))
  }, [area, includeCeilings, includeWoodwork, condition])

  const packages = [
    {
      size: '50 m²',
      price: 'Fra 9.500 – 14.500 kr.',
      title: '50 m² bolig',
      bullets: [
        'Perfekt til mindre lejligheder eller enkeltetager',
        'Hurtig og effektiv udførelse',
        'Standard vægge i normal stand'
      ],
      popular: false
    },
    {
      size: '100 m²',
      price: 'Fra 17.500 – 28.000 kr.',
      title: '100 m² bolig',
      bullets: [
        'Mest populære pakke',
        'Indeholder typisk stue, værelser og gang',
        'Let spartling og fuld afdækning'
      ],
      popular: true
    },
    {
      size: '150 m²',
      price: 'Fra 25.000 – 40.000 kr.',
      title: '150 m² bolig',
      bullets: [
        'Ideel til større lejligheder og villaer',
        'Effektiv stordrift giver bedre m²-pris',
        'Mulighed for tilvalg af lofter og farveskift'
      ],
      popular: false
    }
  ]

  const factors = [
    {
      title: 'Væggens stand',
      desc: 'Huller, revner og ujævnheder kræver mere forarbejde og spartling inden maling.',
      icon: AlertTriangle
    },
    {
      title: 'Antal farveskift',
      desc: 'Skift fra mørke farver til hvid (eller omvendt) kan kræve ekstra lag maling.',
      icon: Paintbrush
    },
    {
      title: 'Loftmaling og træværk',
      desc: 'Maling af lofter, paneler, døre og vindueskarme øger tidsforbruget og materialeforbruget.',
      icon: Sparkles
    },
    {
      title: 'Adgangsforhold og møblering',
      desc: 'Tomme rum med nem adgang gør arbejdet hurtigere end fuldt møblerede rum.',
      icon: Sliders
    },
    {
      title: 'Omfang af spartling',
      desc: 'Om der blot skal pletspartles mindre huller, eller om hele vægge skal fuldspartles og have filt.',
      icon: Info
    }
  ]

  const whyChooseUs = [
    {
      title: 'Fast og gennemsigtig prisstruktur',
      desc: 'Ingen ubehagelige overraskelser eller skjulte gebyrer. Du kender prisen på forhånd.',
      icon: ShieldCheck
    },
    {
      title: 'Erfarne malere med faglig stolthed',
      desc: 'Vores malere går op i detaljen, de lige linjer og den perfekte dækning.',
      icon: Paintbrush
    },
    {
      title: 'Ryddelig og professionel udførelse',
      desc: 'Vi dækker grundigt af og rydder altid pænt op efter os selv hver dag.',
      icon: Sparkles
    },
    {
      title: 'Punktlig levering til aftalt tid',
      desc: 'Vi overholder vores aftaler og tidsplaner, så du kan planlægge din hverdag.',
      icon: Clock
    }
  ]

  const areas = [
    'København', 'Frederiksberg', 'Valby', 'Amager', 'Vanløse', 'Storkøbenhavn og omegn'
  ]

  const specificPrices = [
    {
      title: 'Fuldspartling (2 lag)',
      category: 'Forarbejde',
      price: 'Fra 85 kr.',
      unit: 'pr. m² vægflade',
      desc: 'Giver helt glatte vægge klar til filt eller maling. Perfekt til gipsplader, rå beton eller pudsede vægge.'
    },
    {
      title: 'Opsætning af glasfilt',
      category: 'Vægbeklædning',
      price: 'Fra 75 kr.',
      unit: 'pr. m² vægflade',
      desc: 'Professionel opsætning af ugrundet eller forgrundet glasfilt. Sikrer mod revner og giver en flot, ensartet struktur.'
    },
    {
      title: 'Maling af vægge (2 lag)',
      category: 'Malerarbejde',
      price: 'Fra 65 kr.',
      unit: 'pr. m² vægflade',
      desc: 'Professionel dækkeevne med slidstærk kvalitetsmaling (typisk glans 5, 7 eller 10). Inkl. let pletspartling.'
    },
    {
      title: 'Maling af lofter (2 lag)',
      category: 'Malerarbejde',
      price: 'Fra 55 kr.',
      unit: 'pr. m² loftflade',
      desc: 'Helmat loftmaling (glans 2-3) for et ensartet, refleksfrit resultat uden synlige rullespor.'
    },
    {
      title: 'Maling af fodpaneler & dørkarme',
      category: 'Træværk',
      price: 'Fra 45 kr.',
      unit: 'pr. løbende meter',
      desc: 'Grundig slibning, pletgrunding og 2 lag slidstærk emalje (glans 40 eller 80) for maksimal holdbarhed.'
    },
    {
      title: 'Maling af døre (inkl. karm)',
      category: 'Træværk',
      price: 'Fra 850 kr.',
      unit: 'pr. dør inkl. karm',
      desc: 'Professionel behandling af dørplade og karm. Slibning, mellemstrygning og dæklag for en glat og slidstærk finish.'
    }
  ]

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-white pt-16 pb-12 border-b border-slate-200 overflow-hidden">
        <div className="absolute inset-y-0 right-0 w-1/2 bg-slate-50 hidden lg:block rounded-l-3xl overflow-hidden">
          <img 
            src="https://assets.auxodev.dk/assets/8102f19c-aab0-44cc-aa15-a120b6503274/46fc1f21-a13f-4d04-8b40-c3954618f206.jpg" 
            alt="Flot nymalet stue" 
            className="w-full h-full object-cover opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-transparent to-transparent" />
        </div>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-2xl lg:py-8 space-y-6">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700 border border-sky-100">
              <ShieldCheck className="h-3.5 w-3.5" />
              Gennemsigtige priser uden overraskelser
            </div>
            <h1 className="text-3xl font-semibold sm:text-4xl lg:text-5xl text-slate-900 tracking-tight leading-tight">
              Indvendig malerarbejde i Storkøbenhavn – priser
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Hos os får du professionelt indendørs malerarbejde i hele Storkøbenhavn – udført med fokus på kvalitet, skarpe linjer og holdbare resultater.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              Vi tilbyder gennemsigtige fra-priser, så du hurtigt kan få en idé om budgettet.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <a 
                href="#beregner" 
                className="inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-black transition-all"
              >
                Prøv prisberegneren
              </a>
              <Link 
                to="/kontakt" 
                className="inline-flex items-center justify-center rounded-full bg-white border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-700 hover:text-slate-900 hover:border-slate-400 transition-all"
              >
                Få et gratis tilbud
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Hvad koster indendørs maling Section */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">
                Hvad koster indendørs maling?
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Prisen afhænger af boligens stand, antal rum og forarbejde som spartling og afdækning.
              </p>
              
              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-6 sm:p-8 space-y-4">
                <p className="text-sm font-bold uppercase tracking-wider text-slate-500">Typiske markedspriser i Storkøbenhavn</p>
                <div className="flex flex-col sm:flex-row sm:items-baseline gap-2">
                  <span className="text-3xl sm:text-4xl font-bold text-slate-900">170 – 280 kr.</span>
                  <span className="text-lg text-slate-600 font-medium">pr. m² inkl. moms ved almindeligt malerarbejde</span>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  * Bemærk at m²-prisen refererer til boligens gulvareal, hvilket er standarden i branchen for at gøre det nemt at overskue. Prisen dækker maling af de tilhørende vægge.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm space-y-6">
                <h3 className="font-semibold text-lg text-slate-900">Dette inkluderer typisk:</h3>
                <ul className="space-y-4">
                  {[
                    'Afdækning af gulve og inventar',
                    '2 lag vægmaling',
                    'Mindre reparationer og pletspartling',
                    'Professionel finish'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="rounded-full bg-emerald-50 p-1 text-emerald-600 shrink-0 mt-0.5">
                        <Check className="h-4 w-4" />
                      </div>
                      <span className="text-slate-700 text-sm font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Price Estimator Section */}
      <section id="beregner" className="py-16 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-12">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">
              <Calculator className="h-3.5 w-3.5" />
              Prisberegner
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">
              Beregn et estimat på din maleropgave
            </h2>
            <p className="text-slate-600 text-sm sm:text-base">
              Indtast din boligs størrelse og vælg dine tilvalg for at få et vejledende prisestimat med det samme.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 max-w-5xl mx-auto">
            {/* Calculator Controls */}
            <div className="lg:col-span-7 rounded-2xl bg-white border border-slate-200 p-6 sm:p-8 shadow-sm space-y-6">
              {/* Area Slider */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-bold uppercase tracking-wider text-slate-700">Boligstørrelse (m² gulvareal)</label>
                  <span className="text-lg font-bold text-slate-900 bg-slate-100 px-3 py-1 rounded-lg">{area} m²</span>
                </div>
                <input 
                  type="range" 
                  min="30" 
                  max="250" 
                  step="5"
                  value={area} 
                  onChange={(e) => setArea(parseInt(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-slate-900"
                />
                <div className="flex justify-between text-xs text-slate-400">
                  <span>30 m²</span>
                  <span>100 m²</span>
                  <span>170 m²</span>
                  <span>250 m²</span>
                </div>
              </div>

              {/* Options Checkboxes */}
              <div className="space-y-3 pt-2">
                <label className="text-sm font-bold uppercase tracking-wider text-slate-700 block">Hvad skal males?</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setIncludeCeilings(!includeCeilings)}
                    className={`flex items-center justify-between rounded-xl border p-4 text-left transition-all ${
                      includeCeilings 
                        ? 'border-slate-900 bg-slate-50/50 ring-1 ring-slate-900' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Inkluder lofter</p>
                      <p className="text-xs text-slate-500 mt-0.5">Maling af lofter i alle rum</p>
                    </div>
                    <div className={`rounded-full p-0.5 border ${includeCeilings ? 'bg-slate-900 border-slate-900 text-white' : 'border-slate-300 text-transparent'}`}>
                      <Check className="h-3 w-3" />
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setIncludeWoodwork(!includeWoodwork)}
                    className={`flex items-center justify-between rounded-xl border p-4 text-left transition-all ${
                      includeWoodwork 
                        ? 'border-slate-900 bg-slate-50/50 ring-1 ring-slate-900' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Inkluder træværk</p>
                      <p className="text-xs text-slate-500 mt-0.5">Paneler, døre og karme</p>
                    </div>
                    <div className={`rounded-full p-0.5 border ${includeWoodwork ? 'bg-slate-900 border-slate-900 text-white' : 'border-slate-300 text-transparent'}`}>
                      <Check className="h-3 w-3" />
                    </div>
                  </button>
                </div>
              </div>

              {/* Condition Selector */}
              <div className="space-y-3 pt-2">
                <label className="text-sm font-bold uppercase tracking-wider text-slate-700 block">Væggenes nuværende stand</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'god', label: 'God stand', desc: 'Få huller' },
                    { id: 'middel', label: 'Middel stand', desc: 'Standard' },
                    { id: 'dårlig', label: 'Dårlig stand', desc: 'Kræver spartel' }
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setCondition(opt.id)}
                      className={`rounded-xl border p-3 text-center transition-all ${
                        condition === opt.id 
                          ? 'border-slate-900 bg-slate-900 text-white shadow-sm' 
                          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      <p className="text-xs sm:text-sm font-semibold">{opt.label}</p>
                      <p className={`text-[10px] mt-0.5 ${condition === opt.id ? 'text-slate-300' : 'text-slate-400'}`}>{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Calculator Result */}
            <div className="lg:col-span-5 flex flex-col justify-between rounded-2xl bg-slate-900 text-white p-6 sm:p-8 shadow-md relative overflow-hidden">
              <div className="absolute top-0 right-0 -mt-6 -mr-6 h-32 w-32 rounded-full bg-white/5 blur-2xl" />
              
              <div className="space-y-6 relative z-10">
                <div>
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Dit Prisestimat</span>
                  <h3 className="text-lg font-semibold mt-1">Vejledende pris inkl. moms</h3>
                </div>

                <div className="space-y-1">
                  <p className="text-xs text-slate-400">Estimeret prisinterval:</p>
                  <p className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                    {estimatedMin.toLocaleString('da-DK')} – {estimatedMax.toLocaleString('da-DK')} kr.
                  </p>
                </div>

                <div className="border-t border-white/10 pt-4 space-y-3 text-xs text-slate-300">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-sky-400 shrink-0" />
                    <span>Inkluderer fuld afdækning & 2 lag maling</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-sky-400 shrink-0" />
                    <span>Professionelle malere & kvalitetsmaling</span>
                  </div>
                  {includeCeilings && (
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-sky-400 shrink-0" />
                      <span>Inkluderer maling af lofter</span>
                    </div>
                  )}
                  {includeWoodwork && (
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-sky-400 shrink-0" />
                      <span>Inkluderer maling af træværk</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-8 relative z-10 space-y-3">
                <Link
                  to="/kontakt"
                  state={{ service: 'Indendørs Maling', message: `Jeg ønsker et tilbud på indvendig maling af min bolig på ${area} m². Væggenes stand er ${condition === 'god' ? 'god' : condition === 'middel' ? 'middel' : 'dårlig'}.${includeCeilings ? ' Jeg ønsker også lofter malet.' : ''}${includeWoodwork ? ' Jeg ønsker også træværk malet.' : ''}` }}
                  className="flex w-full items-center justify-center rounded-full bg-white py-3.5 text-center text-sm font-semibold text-slate-900 hover:bg-slate-100 transition-colors shadow-sm"
                >
                  Få et præcist tilbud nu
                  <ArrowRight className="ml-1.5 h-4 w-4" />
                </Link>
                <p className="text-[10px] text-center text-slate-400">
                  Estimater er vejledende. Vi giver altid et gratis, uforpligtende og fast tilbud efter en kort snak eller besigtigelse.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pakkepriser Section */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Overskuelige pakker</span>
            <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">
              Pakkepriser – fra-priser inkl. moms
            </h2>
            <p className="text-slate-600 text-sm sm:text-base">
              Herunder har vi samlet tre af vores mest populære boligstørrelser, så du hurtigt kan danne dig et overblik over prisniveauet.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3 max-w-6xl mx-auto">
            {packages.map((pkg, index) => (
              <div 
                key={index}
                className={`relative flex flex-col justify-between rounded-2xl border p-6 sm:p-8 transition-all duration-300 ${
                  pkg.popular 
                    ? 'border-slate-900 bg-white shadow-lg ring-1 ring-slate-900 scale-105 z-10 md:-translate-y-2' 
                    : 'border-slate-200 bg-slate-50 hover:bg-white hover:shadow-md'
                }`}
              >
                {pkg.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-slate-900 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                    Mest Populære
                  </span>
                )}

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">{pkg.title}</h3>
                    <p className="text-2xl font-extrabold text-slate-900 mt-2">{pkg.price}</p>
                  </div>

                  <ul className="space-y-3 border-t border-slate-200 pt-5">
                    {pkg.bullets.map((bullet, idx) => (
                      <li key={idx} className="flex items-start gap-2.5">
                        <Check className="h-4 w-4 text-slate-900 shrink-0 mt-0.5" />
                        <span className="text-slate-600 text-xs sm:text-sm leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-8">
                  <Link
                    to="/kontakt"
                    state={{ service: 'Indendørs Maling', message: `Jeg er interesseret i jeres pakke til en ${pkg.title}.` }}
                    className={`flex w-full items-center justify-center rounded-full py-3 text-center text-xs sm:text-sm font-semibold transition-all ${
                      pkg.popular 
                        ? 'bg-slate-900 text-white hover:bg-black' 
                        : 'bg-white border border-slate-300 text-slate-700 hover:border-slate-400 hover:text-slate-900'
                    }`}
                  >
                    Få et tilbud
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fra-priser på specifikke ydelser Section */}
      <section className="py-16 bg-slate-50 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Prisliste pr. m² & enhed</span>
            <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">
              Fra-priser på specifikke ydelser
            </h2>
            <p className="text-slate-600 text-sm sm:text-base">
              For at gøre det nemt for dig at beregne et budget, har vi samlet vores vejledende kvadratmeter- og enhedspriser herunder. Alle priser er inkl. moms, materialer og professionel udførelse.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {specificPrices.slice(0, showAllPrices ? specificPrices.length : 3).map((item, index) => (
              <div key={index} className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col justify-between shadow-xs hover:shadow-md transition-all">
                <div className="space-y-4">
                  <div className="flex justify-between items-start gap-4">
                    <span className="inline-flex items-center rounded-md bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-800">
                      {item.category}
                    </span>
                    <div className="text-right">
                      <p className="text-xs text-slate-400 uppercase font-bold tracking-wider">Fra-pris</p>
                      <p className="text-xl font-extrabold text-slate-900">{item.price}</p>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">{item.title}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{item.unit}</p>
                  </div>
                  <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
                <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                  <span className="flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    Inkl. materialer
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    Inkl. moms
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <button
              onClick={() => setShowAllPrices(!showAllPrices)}
              className="inline-flex items-center justify-center rounded-full bg-white border border-slate-300 px-8 py-3.5 text-sm font-semibold text-slate-700 shadow-xs hover:border-slate-400 hover:text-slate-900 transition-all cursor-pointer"
            >
              {showAllPrices ? 'Vis færre priser' : 'Vis alle priser'}
              <i className={`fa-solid fa-chevron-down ml-2 transition-transform duration-300 ${showAllPrices ? 'rotate-180' : ''}`} />
            </button>
          </div>

          <div className="mt-8 text-center">
            <p className="text-xs text-slate-500 max-w-2xl mx-auto leading-relaxed">
              * Bemærk: Det endelige tilbud afhænger altid af væggenes beskaffenhed, adgangsforhold og det samlede areal. Ved større opgaver (f.eks. hele huse eller lejligheder) tilbyder vi ofte fordelagtige samlede m²-priser.
            </p>
          </div>
        </div>
      </section>

      {/* Hvad påvirker prisen Section */}
      <section className="py-16 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Vigtige faktorer</span>
            <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">
              Hvad påvirker prisen?
            </h2>
            <p className="text-slate-600 text-sm sm:text-base">
              Flere faktorer kan justere den endelige pris:
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5 max-w-6xl mx-auto">
            {factors.map((factor, index) => {
              const IconComponent = factor.icon
              return (
                <div key={index} className="rounded-2xl bg-white border border-slate-200 p-6 shadow-xs flex flex-col space-y-3">
                  <div className="rounded-xl bg-slate-100 p-2.5 text-slate-900 w-fit">
                    <IconComponent className="h-5 w-5" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-sm sm:text-base">{factor.title}</h3>
                  <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">{factor.desc}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Derfor vælger kunder os Section */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Vores værdier</span>
            <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">
              Derfor vælger kunder os
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto">
            {whyChooseUs.map((item, index) => {
              const IconComponent = item.icon
              return (
                <div key={index} className="flex flex-col items-start space-y-3 p-2">
                  <div className="rounded-full bg-slate-900 p-3 text-white">
                    <IconComponent className="h-6 w-6" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-base sm:text-lg">{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Arbejdsområde Section */}
      <section className="py-20 border-b border-slate-200 bg-slate-50/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Venstre kolonne: Tekst og liste */}
            <div className="lg:col-span-6 space-y-6 text-left">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-800">
                <MapPin className="h-3.5 w-3.5 text-slate-900" />
                Geografisk dækning
              </div>
              <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Hvor udfører vi malerarbejde?
              </h2>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
                Vi dækker <strong className="text-slate-900 font-semibold">udelukkende Storkøbenhavn og omegn</strong>. For at sikre den højeste kvalitet, hurtig udrykning og tæt personlig kontakt, har vi valgt at fokusere 100% på vores nærområde.
              </p>
              
              <div className="space-y-3 pt-2">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Vores primære arbejdsområder:
                </h3>
                <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {areas.map((area, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      <i className="fa-solid fa-circle-check text-slate-900 text-xs shrink-0" />
                      <span>{area}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Højre kolonne: Visuelt dækningskort */}
            <div className="lg:col-span-6">
              <div className="relative overflow-hidden">
                <div className="relative">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/shutterstock-2355857759.jpg" 
                    alt="Dækningskort - Vi dækker Storkøbenhavn og omegn" 
                    className="w-full h-auto rounded-2xl object-cover"
                  />
                  <div className="absolute bottom-4 left-4 right-4 bg-slate-900/90 backdrop-blur-xs text-white p-4 rounded-xl shadow-lg border border-slate-800 text-left">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="h-2 w-2 rounded-full bg-sky-400 animate-pulse" />
                      <span className="text-xs font-bold uppercase tracking-wider text-sky-400">Vi dækker her:</span>
                    </div>
                    <h4 className="text-sm font-bold">Storkøbenhavn</h4>
                    <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                      København, Frederiksberg, Valby, Amager, Vanløse & omegn.
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{ backgroundImage: "url('https://assets.auxodev.dk/assets/8102f19c-aab0-44cc-aa15-a120b6503274/e55acbc0-f265-44e6-a4f5-18a1e53fa429.jpg')" }} />
        
        <div className="mx-auto max-w-4xl px-4 text-center space-y-6 relative z-10">
          <h2 className="text-3xl font-semibold sm:text-4xl">
            Få en præcis pris på din opgave
          </h2>
          <p className="text-slate-300 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Alle boliger er forskellige – derfor giver vi altid et gratis og uforpligtende tilbud baseret på dine konkrete rum.
          </p>
          <div className="pt-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center rounded-full bg-white px-8 py-4 text-base font-semibold text-slate-900 hover:bg-slate-100 transition-colors shadow-md"
            >
              Få gratis tilbud nu
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
